// #765 Story D View 2 — per-user engagement trajectory.
//
// Weekly buckets for the last 12 weeks: engagement avg + call count +
// mood average. Renders an inline SVG chart (no chart-library dep —
// matches the existing AdminAnalytics CSS-bar approach but a real
// line chart is cleaner here for the time-series shape).
//
// "Slipping" badge surfaces when the API flags it: last-4-weeks
// engagement avg is at least 15 points below the prior-8-weeks avg
// AND both windows have ≥2 weeks of data. Leading indicator for
// elderly-user disengagement; intervention 2-3 weeks before churn.
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { adminApi } from './adminApi';
import { userSubPageStyles, adminFetch } from './userSubPageStyles';

const VERSION = 'v1.0.0';

// Match Story B's threshold-relevant colours / engagement bucketing.
function engagementColor(w) {
  if (w == null) return '#ddd';
  if (w >= 70) return '#28A745';
  if (w >= 30) return '#E8845A';
  if (w >= 1)  return '#888';
  return '#DC3545';
}

function moodLabel(avg) {
  if (avg == null) return '—';
  // Same numeric mapping the backend uses: cheerful=4 / neutral=3 / quiet=2 / low=1.
  const r = Math.round(avg);
  return ({ 4: 'cheerful', 3: 'neutral', 2: 'quiet', 1: 'low' })[r] || avg.toFixed(1);
}

function fmtWeek(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// Mini inline SVG line chart for engagement over weeks. 600x160 viewBox.
function TrajectoryChart({ weeks }) {
  if (!weeks || weeks.length === 0) {
    return <div className="empty-state">No data yet.</div>;
  }
  const W = 600, H = 160, P = 30;
  const valid = weeks.map(w => w.engagement_avg);
  // X positions: evenly spaced across the available width.
  const xs = weeks.map((_, i) =>
    P + (i * (W - 2 * P)) / Math.max(1, weeks.length - 1)
  );
  // Y mapping: 0-100 → bottom-to-top.
  const yFor = v => H - P - (Math.max(0, Math.min(100, v)) / 100) * (H - 2 * P);
  // Skip nulls; render the polyline with only the present points.
  const points = xs
    .map((x, i) => valid[i] == null ? null : `${x.toFixed(1)},${yFor(valid[i]).toFixed(1)}`)
    .filter(Boolean)
    .join(' ');

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{
      background: '#FAF8F5', borderRadius: 8,
    }}>
      {/* Y grid lines at 30 + 70 (Story B's behaviour-range bucket boundaries). */}
      {[30, 70].map(y => (
        <g key={y}>
          <line
            x1={P} y1={yFor(y)} x2={W - P} y2={yFor(y)}
            stroke="#e5e5e5" strokeWidth="1" strokeDasharray="3,3"
          />
          <text
            x={P - 6} y={yFor(y) + 3} textAnchor="end"
            fill="#aaa" fontSize="10"
          >{y}</text>
        </g>
      ))}
      {/* Engagement line. */}
      {points && (
        <polyline fill="none" stroke="#E8845A" strokeWidth="2" points={points} />
      )}
      {/* Per-point dots, coloured by bucket. */}
      {weeks.map((w, i) => w.engagement_avg != null && (
        <circle
          key={i} cx={xs[i]} cy={yFor(w.engagement_avg)} r="3"
          fill={engagementColor(w.engagement_avg)}
        />
      ))}
      {/* X labels: first + middle + last. */}
      {weeks.length > 0 && (
        <>
          <text x={xs[0]} y={H - 10} fill="#888" fontSize="10" textAnchor="middle">
            {fmtWeek(weeks[0].week_start)}
          </text>
          {weeks.length > 2 && (
            <text x={xs[Math.floor(weeks.length / 2)]} y={H - 10} fill="#888" fontSize="10" textAnchor="middle">
              {fmtWeek(weeks[Math.floor(weeks.length / 2)].week_start)}
            </text>
          )}
          {weeks.length > 1 && (
            <text x={xs[xs.length - 1]} y={H - 10} fill="#888" fontSize="10" textAnchor="middle">
              {fmtWeek(weeks[weeks.length - 1].week_start)}
            </text>
          )}
        </>
      )}
    </svg>
  );
}

export default function AdminUserEngagement() {
  const { id } = useParams();
  const [user, setUser] = useState(null);
  const [trajectory, setTrajectory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const u = await adminApi.getUser(id);
        if (cancelled) return;
        setUser(u);
        const r = await adminFetch(
          `/api/admin/users/${id}/engagement?weeks=12`
        );
        if (cancelled) return;
        setTrajectory(r || { weeks: [], slipping: false });
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const weeks = trajectory?.weeks || [];
  const slipping = !!trajectory?.slipping;

  // Headline metrics — last week's snapshot.
  const last = weeks[weeks.length - 1];

  return (
    <AdminLayout title="Engagement">
      <style>{userSubPageStyles}</style>

      <Link to={`/admin/users/${id}`} className="back-link">← Back to user</Link>

      {loading && <p className="loading-state">Loading…</p>}
      {error && <p className="error-state">Error: {error}</p>}

      {user && (
        <div className="page-header">
          <div className="page-title">
            {user.name || '(unnamed)'}<span className="ver">{VERSION}</span>
            {slipping && (
              <span style={{
                marginLeft: 12,
                background: '#FFEBEE', color: '#C62828',
                padding: '4px 10px', borderRadius: 14,
                fontSize: '0.78rem', fontWeight: 700,
                letterSpacing: '0.04em', textTransform: 'uppercase',
              }} title="Last 4 weeks' engagement avg dropped ≥15 points vs prior 8 weeks">
                ⚠ Slipping
              </span>
            )}
          </div>
          <div className="page-subtitle">
            {user.email} · Engagement trajectory (last 12 weeks)
          </div>
        </div>
      )}

      {trajectory && (
        <>
          <div className="section-title">
            Weekly engagement
            <span className="section-sub">
              avg score across played cards · 0–100 scale (Story B engagement metric)
            </span>
          </div>
          <div style={{ marginBottom: 24 }}>
            <TrajectoryChart weeks={weeks} />
          </div>

          <div className="section-title">
            Per-week breakdown
            <span className="section-sub">engagement avg · call count · mood label</span>
          </div>
          <div className="data-grid">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Week of</th>
                  <th>Engagement</th>
                  <th>Calls</th>
                  <th>Mood</th>
                </tr>
              </thead>
              <tbody>
                {weeks.length === 0 && (
                  <tr><td colSpan={4} className="empty-state">No data yet.</td></tr>
                )}
                {weeks.map((w, i) => (
                  <tr key={w.week_start || i}>
                    <td style={{ whiteSpace: 'nowrap' }}>{fmtWeek(w.week_start)}</td>
                    <td>
                      {w.engagement_avg != null ? (
                        <span style={{
                          fontWeight: 700,
                          color: engagementColor(w.engagement_avg),
                        }}>
                          {Math.round(w.engagement_avg)}
                        </span>
                      ) : <span style={{ color: '#ccc' }}>—</span>}
                    </td>
                    <td>{w.call_count}</td>
                    <td style={{ color: '#666' }}>{moodLabel(w.mood_avg)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {last && (
            <p style={{ marginTop: 14, color: '#888', fontSize: '0.85rem' }}>
              Latest week ({fmtWeek(last.week_start)}):
              {' '}engagement {last.engagement_avg != null
                ? Math.round(last.engagement_avg) : '—'},
              {' '}{last.call_count} call{last.call_count === 1 ? '' : 's'},
              {' '}mood {moodLabel(last.mood_avg)}.
            </p>
          )}
        </>
      )}
    </AdminLayout>
  );
}
